import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import './ToastNotification.css';

// Toast types with proper TypeScript-like documentation
/**
 * Available toast notification types
 * @readonly
 * @enum {string}
 */
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  WARNING: 'warning',
  LOADING: 'loading' // New loading type for async operations
};

// Toast positioning options
/**
 * Available toast notification positions
 * @readonly
 * @enum {string}
 */
export const TOAST_POSITIONS = {
  TOP_RIGHT: 'top-right',
  TOP_LEFT: 'top-left',
  BOTTOM_RIGHT: 'bottom-right',
  BOTTOM_LEFT: 'bottom-left',
  TOP_CENTER: 'top-center',
  BOTTOM_CENTER: 'bottom-center'
};

/**
 * Configuration object for toast system
 * @typedef {Object} ToastConfig
 * @property {number} maxToasts - Maximum number of toasts to display at once
 * @property {number} defaultDuration - Default duration in ms before auto-hiding
 * @property {string} defaultPosition - Default position for toasts
 * @property {boolean} pauseOnHover - Whether to pause timer when hovering
 * @property {boolean} closeOnClick - Whether clicking on toast dismisses it
 */

// Default configuration
const DEFAULT_CONFIG = {
  maxToasts: 1,
  defaultDuration: 3000,
  defaultPosition: TOAST_POSITIONS.TOP_RIGHT,
  pauseOnHover: true,
  closeOnClick: true,
  allowDuplicates: false
};

// Toast container component
function ToastContainer({ config = DEFAULT_CONFIG }) {
  const [toasts, setToasts] = useState([]);
  const [toastRegistry, setToastRegistry] = useState({});
  const [idCounter, setIdCounter] = useState(1);
  
  // Use useMemo to prevent recreating mergedConfig on every render
  const mergedConfig = useMemo(() => {
    return { ...DEFAULT_CONFIG, ...config };
  }, [config]);
  
  const timerRefsMap = useRef(new Map());
  
  // Group toasts by position
  const toastsByPosition = useMemo(() => {
    return toasts.reduce((acc, toast) => {
      const position = toast.position || mergedConfig.defaultPosition;
      if (!acc[position]) {
        acc[position] = [];
      }
      acc[position].push(toast);
      return acc;
    }, {});
  }, [toasts, mergedConfig.defaultPosition]);

  // Remove toast by id
  const removeToast = useCallback((id) => {
    setToasts(currentToasts => currentToasts.filter(toast => toast.id !== id));
    
    // Also remove from registry when toast is dismissed
    setToastRegistry(registry => {
      const newRegistry = { ...registry };
      Object.keys(newRegistry).forEach(key => {
        if (newRegistry[key] === id) {
          delete newRegistry[key];
        }
      });
      return newRegistry;
    });
    
    // Clear any timers associated with this toast
    if (timerRefsMap.current.has(id)) {
      clearTimeout(timerRefsMap.current.get(id));
      timerRefsMap.current.delete(id);
    }
  }, []);

  // Update toast by id
  const updateToast = useCallback((id, updates) => {
    setToasts(currentToasts => 
      currentToasts.map(toast => 
        toast.id === id ? { ...toast, ...updates } : toast
      )
    );
  }, []);

  // Create a hash key for toast deduplication
  const createToastKey = useCallback((message, type, options = {}) => {
    // Create a normalized message string to avoid slight variations causing duplicates
    const normalizedMessage = String(message).trim();
    
    // Only include essential options that would make a toast visually different
    const essentialOptions = {
      position: options.position || mergedConfig.defaultPosition
    };
    
    return `${type}:${normalizedMessage}:${JSON.stringify(essentialOptions)}`;
  }, [mergedConfig.defaultPosition]);

  // Pause toast timer on hover
  const pauseTimer = useCallback((id) => {
    if (!mergedConfig.pauseOnHover) return;
    
    const toast = toasts.find(t => t.id === id);
    if (!toast || !toast.timerId) return;
    
    // Clear the existing timer
    if (timerRefsMap.current.has(id)) {
      clearTimeout(timerRefsMap.current.get(id));
      timerRefsMap.current.delete(id);
    }
    
    // Store remaining time
    const elapsedTime = Date.now() - toast.startTime;
    const remainingTime = toast.autoHideDuration - elapsedTime;
    
    // Update toast with remaining time
    updateToast(id, { 
      isPaused: true, 
      remainingTime: remainingTime > 0 ? remainingTime : 0 
    });
  }, [toasts, updateToast, mergedConfig.pauseOnHover]);

  // Resume toast timer after hover ends
  const resumeTimer = useCallback((id) => {
    if (!mergedConfig.pauseOnHover) return;
    
    const toast = toasts.find(t => t.id === id);
    if (!toast || !toast.isPaused) return;
    
    // Create a new timer with remaining time
    if (toast.remainingTime && toast.remainingTime > 0) {
      const newTimerId = setTimeout(() => removeToast(id), toast.remainingTime);
      timerRefsMap.current.set(id, newTimerId);
      
      // Update toast state
      updateToast(id, { 
        isPaused: false, 
        startTime: Date.now(),
        timerId: newTimerId
      });
    }
  }, [toasts, updateToast, removeToast, mergedConfig.pauseOnHover]);

  // Add toast to the stack with deduplication
  const addToast = useCallback((message, type, options = {}) => {
    const {
      duration,
      position,
      icon,
      allowDuplicate = mergedConfig.allowDuplicates,
      action,
      onClose,
      progress = true
    } = options;
    
    const autoHideDuration = duration !== undefined ? duration : mergedConfig.defaultDuration;
    
    // Create a unique key for this type of toast
    const toastKey = createToastKey(message, type, options);
    
    // Check if this exact toast is already showing
    if (!allowDuplicate && toastRegistry[toastKey]) {
      const existingId = toastRegistry[toastKey];
      const existingToast = toasts.find(t => t.id === existingId);
      
      // Skip if we can't find the toast - it might have been removed already
      if (!existingToast) {
        // Remove stale registry entry
        setToastRegistry(registry => {
          const newRegistry = { ...registry };
          delete newRegistry[toastKey];
          return newRegistry;
        });
      } else {
        // Clear any existing timeout for this toast
        if (timerRefsMap.current.has(existingId)) {
          clearTimeout(timerRefsMap.current.get(existingId));
          timerRefsMap.current.delete(existingId);
        }
        
        // Refresh the toast with updated timestamp and new timer
        const newTimerId = autoHideDuration > 0 
          ? setTimeout(() => removeToast(existingId), autoHideDuration)
          : null;
          
        if (newTimerId) {
          timerRefsMap.current.set(existingId, newTimerId);
        }
        
        // Update the toast to reset its appearance and timer
        updateToast(existingId, { 
          message, // Update message in case it changed slightly
          timerId: newTimerId, 
          startTime: Date.now(),
          isPaused: false,
          remainingTime: autoHideDuration,
          timestamp: Date.now() // Important to update timestamp
        });
        
        return existingId;
      }
    }

    // Create a new toast with a truly unique ID
    // Using UUID-like format to ensure uniqueness across renders
    const uniqueId = `toast-${Date.now()}-${Math.floor(Math.random() * 10000)}-${idCounter}`;
    setIdCounter(prev => prev + 2);  // Increment the counter for next use
    
    let timerId = null;
    
    if (autoHideDuration > 0) {
      timerId = setTimeout(() => {
        // Call onClose callback if provided
        if (typeof onClose === 'function') {
          try {
            onClose();
          } catch (error) {
            console.error('Error in toast onClose callback:', error);
          }
        }
        removeToast(uniqueId);
      }, autoHideDuration);
      
      timerRefsMap.current.set(uniqueId, timerId);
    }
    
    const newToast = { 
      id: uniqueId, 
      message,
      type, 
      autoHideDuration,
      timerId,
      timestamp: Date.now(),
      startTime: Date.now(),
      position: position || mergedConfig.defaultPosition,
      icon,
      action,
      progress,
      isPaused: false,
      remainingTime: autoHideDuration
    };

    setToasts(currentToasts => [...currentToasts, newToast]);
    
    // Register this toast to prevent duplicates
    setToastRegistry(registry => ({
      ...registry,
      [toastKey]: uniqueId
    }));

    return uniqueId;
  }, [
    toasts,
    removeToast, 
    toastRegistry, 
    createToastKey, 
    idCounter, 
    updateToast,
    mergedConfig.defaultDuration, 
    mergedConfig.defaultPosition,
    mergedConfig.allowDuplicates
  ]);

  // Clear all toasts
  const clearAllToasts = useCallback(() => {
    // Clear all timeouts
    toasts.forEach(toast => {
      if (timerRefsMap.current.has(toast.id)) {
        clearTimeout(timerRefsMap.current.get(toast.id));
        timerRefsMap.current.delete(toast.id);
      }
    });
    
    setToasts([]);
    setToastRegistry({});
  }, [toasts]);

  // Handle progress indicator
  const getProgressStyle = useCallback((toast) => {
    if (!toast.progress || toast.isPaused) return {};
    
    const elapsed = Date.now() - toast.startTime;
    const total = toast.autoHideDuration;
    const percentage = Math.max(0, 100 - (elapsed / total * 100));
    
    return {
      width: `${percentage}%`
    };
  }, []);

  // Effect to limit maximum number of toasts
  useEffect(() => {
    // Limit the maximum number of toasts to prevent UI overload
    if (toasts.length > mergedConfig.maxToasts) {
      // Find toasts by position
      const positionCounts = {};
      
      // Count toasts by position
      toasts.forEach(toast => {
        const position = toast.position || mergedConfig.defaultPosition;
        positionCounts[position] = (positionCounts[position] || 0) + 1;
      });
      
      // Find positions with too many toasts
      Object.keys(positionCounts).forEach(position => {
        if (positionCounts[position] > Math.ceil(mergedConfig.maxToasts / 2)) {
          // Get all toasts for this position
          const positionToasts = toasts.filter(toast => 
            (toast.position || mergedConfig.defaultPosition) === position
          );
          
          // Find the oldest toast
          if (positionToasts.length > 0) {
            const oldestToast = positionToasts.reduce((oldest, toast) => 
              (toast.timestamp < oldest.timestamp) ? toast : oldest
            , positionToasts[0]);
            
            if (oldestToast) {
              removeToast(oldestToast.id);
            }
          }
        }
      });
    }
  }, [toasts, removeToast, mergedConfig.maxToasts, mergedConfig.defaultPosition]);

  // Create loading toast with promise tracking
  const createLoadingToast = useCallback(async (message, promise, options = {}) => {
    const loadingId = addToast(message, TOAST_TYPES.LOADING, {
      ...options,
      duration: 0, // Indefinite duration for loading toasts
      progress: false
    });
    
    try {
      const result = await promise;
      
      // Success toast
      const successMsg = options.successMessage || 'Operation completed successfully';
      updateToast(loadingId, {
        type: TOAST_TYPES.SUCCESS,
        message: successMsg,
        autoHideDuration: options.successDuration || mergedConfig.defaultDuration,
        progress: true,
        startTime: Date.now()
      });
      
      // Set up auto-close timer
      const timerId = setTimeout(() => removeToast(loadingId), 
        options.successDuration || mergedConfig.defaultDuration);
      timerRefsMap.current.set(loadingId, timerId);
      
      return result;
    } catch (error) {
      // Error toast
      const errorMsg = options.errorMessage || `Error: ${error.message || 'Operation failed'}`;
      updateToast(loadingId, {
        type: TOAST_TYPES.ERROR,
        message: errorMsg,
        autoHideDuration: options.errorDuration || mergedConfig.defaultDuration,
        progress: true,
        startTime: Date.now()
      });
      
      // Set up auto-close timer
      const timerId = setTimeout(() => removeToast(loadingId), 
        options.errorDuration || mergedConfig.defaultDuration);
      timerRefsMap.current.set(loadingId, timerId);
      
      throw error;
    }
  }, [addToast, updateToast, removeToast, mergedConfig.defaultDuration]);

  // Setup global toast API
  useEffect(() => {
    // Store a reference to timerRefsMap.current for cleanup
    const timerRefsMapCurrent = timerRefsMap.current;
    
    // Enhanced toast API with promise support, positioning and more options
    window.toast = {
      success: (message, options) => addToast(message, TOAST_TYPES.SUCCESS, options),
      error: (message, options) => addToast(message, TOAST_TYPES.ERROR, options),
      info: (message, options) => addToast(message, TOAST_TYPES.INFO, options),
      warning: (message, options) => addToast(message, TOAST_TYPES.WARNING, options),
      loading: (message, options) => addToast(message, TOAST_TYPES.LOADING, { 
        ...options, 
        duration: 0, // Indefinite duration for loading
        progress: false
      }),
      promise: createLoadingToast,
      update: updateToast,
      remove: removeToast,
      clearAll: clearAllToasts,
      // Configuration
      getConfig: () => ({ ...mergedConfig }),
      positions: TOAST_POSITIONS,
      types: TOAST_TYPES
    };

    // Error handling for toast operations
    const originalToastMethods = {
      success: window.toast.success,
      error: window.toast.error,
      info: window.toast.info,
      warning: window.toast.warning,
      loading: window.toast.loading,
      promise: window.toast.promise,
      update: window.toast.update
    };

    // Wrap toast methods with error handling
    Object.keys(originalToastMethods).forEach(method => {
      window.toast[method] = (...args) => {
        try {
          return originalToastMethods[method](...args);
        } catch (error) {
          console.error(`Error in toast.${method}:`, error);
          // Don't recursively call toast.error as it could cause an infinite loop
          if (method !== 'error') {
            window.toast.error(`Failed to show notification: ${error.message}`);
          }
          return null;
        }
      };
    });

    return () => {
      // Clean up on unmount - use the stored reference
      toasts.forEach(toast => {
        if (timerRefsMapCurrent.has(toast.id)) {
          clearTimeout(timerRefsMapCurrent.get(toast.id));
          timerRefsMapCurrent.delete(toast.id);
        }
      });
      delete window.toast;
    };
  }, [
    addToast, 
    removeToast, 
    clearAllToasts, 
    toasts, 
    updateToast, 
    createLoadingToast, 
    mergedConfig
  ]);

  return (
    <>
      {/* Render toast containers for each position */}
      {Object.keys(TOAST_POSITIONS).map(posKey => {
        const position = TOAST_POSITIONS[posKey];
        const positionToasts = toastsByPosition[position] || [];
        
        return (
          <div key={position} className={`toast-container toast-${position}`}>
            {positionToasts.map(toast => (
              <div
                key={toast.id}
                className={`toast toast-${toast.type}`}
                onClick={() => mergedConfig.closeOnClick && removeToast(toast.id)}
                onMouseEnter={() => pauseTimer(toast.id)}
                onMouseLeave={() => resumeTimer(toast.id)}
                role="alert"
                aria-live={toast.type === TOAST_TYPES.ERROR ? 'assertive' : 'polite'}
              >
                {toast.icon && (
                  <div className="toast-icon">
                    {toast.icon}
                  </div>
                )}
                <div className="toast-content">
                  <div className="toast-message">{toast.message}</div>
                  {toast.action && (
                    <div className="toast-action">
                      {toast.action}
                    </div>
                  )}
                </div>
                <button
                  className="toast-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeToast(toast.id);
                  }}
                  aria-label="Close notification"
                >
                  ×
                </button>
                {toast.progress && toast.autoHideDuration > 0 && (
                  <div className="toast-progress-container">
                    <div 
                      className="toast-progress-bar" 
                      style={getProgressStyle(toast)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}

/**
 * Initialize toast system using React 18's createRoot
 * @param {ToastConfig} [config] - Configuration options for toast system
 * @returns {boolean} Success status
 */
export function initToastSystem(config) {
  try {
    let toastRoot = document.getElementById('toast-root');

    if (!toastRoot) {
      toastRoot = document.createElement('div');
      toastRoot.id = 'toast-root';
      document.body.appendChild(toastRoot);

      const root = ReactDOM.createRoot(toastRoot);
      root.render(<ToastContainer config={config} />);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Failed to initialize toast system:', error);
    return false;
  }
}

/**
 * Set up event listeners for automatic toast notifications
 * @param {Object} options - Configuration options for listeners
 * @returns {boolean} Success status
 */
export function setupToastListeners(options = {}) {
  const {
    rateLimit = 1000,
    eventName = 'cardAction',
    customActions
  } = options;

  const actionPatterns = customActions || [
    { action: 'add', success: 'Card added successfully', error: 'Failed to add card' },
    { action: 'edit', success: 'Card updated successfully', error: 'Failed to update card' },
    { action: 'delete', success: 'Card deleted successfully', error: 'Failed to delete card' },
    { action: 'archive', success: 'Card archived successfully', error: 'Failed to archive card' },
    { action: 'unarchive', success: 'Card restored successfully', error: 'Failed to restore card' },
    { action: 'duplicate', success: 'Card duplicated successfully', error: 'Failed to duplicate card' },
    { action: 'import', success: 'Cards imported successfully', error: 'Failed to import cards' },
    { action: 'export', success: 'Cards exported successfully', error: 'Failed to export cards' }
  ];

  // Rate limiting for each action type
  const actionTimestamps = {};
  const RATE_LIMIT_MS = rateLimit;

  window.addEventListener(eventName, (e) => {
    const { action, status, message, position, duration } = e.detail;
    
    // Create a key for this specific action and status
    const actionKey = `${action}:${status}`;
    const now = Date.now();
    
    // Check if this action is being rate limited
    if (actionTimestamps[actionKey] && (now - actionTimestamps[actionKey] < RATE_LIMIT_MS)) {
      console.log(`Rate limiting ${actionKey} toast`);
      return;
    }
    
    // Update timestamp for this action
    actionTimestamps[actionKey] = now;
    
    const toastOptions = {
      position,
      duration
    };
    
    if (status === 'success') {
      window.toast.success(message || `${action} successful`, toastOptions);
    } else if (status === 'error') {
      window.toast.error(message || `${action} failed`, toastOptions);
    } else if (status === 'info') {
      window.toast.info(message || `${action} info`, toastOptions);
    } else if (status === 'warning') {
      window.toast.warning(message || `${action} warning`, toastOptions);
    }
  });

  /**
   * Dispatch a card action event with enhanced options
   * @param {string} action - The action being performed
   * @param {string} status - Status of the action ('success', 'error', etc)
   * @param {string} [message] - Custom message to display
   * @param {Object} [options] - Additional options
   */
  window.dispatchCardAction = (action, status, message, options = {}) => {
    try {
      const { 
        context = {}, 
        position, 
        duration,
        icon
      } = options;
      
      const pattern = actionPatterns.find(p => p.action === action);
      let defaultMessage = '';
      
      if (pattern) {
        defaultMessage = status === 'success' ? pattern.success : pattern.error;
      } else {
        defaultMessage = status === 'success' ? `${action} successful` : `${action} failed`;
      }
      
      // Include more context in the message if provided
      const enhancedMessage = message || defaultMessage;
      
      const event = new CustomEvent(eventName, {
        detail: {
          action,
          status,
          message: enhancedMessage,
          timestamp: Date.now(),
          context,
          position,
          duration,
          icon
        }
      });
      window.dispatchEvent(event);
    } catch (error) {
      console.error('Error dispatching card action:', error);
      // Direct call to toast.error as a fallback
      if (window.toast) {
        window.toast.error(`Failed to report ${action} action`);
      }
    }
  };

  // Promise-based action helper
  window.dispatchAsyncCardAction = async (action, promise, options = {}) => {
    const { 
      loadingMessage = `${action} in progress...`,
      successMessage,
      errorMessage,
      position,
      successDuration,
      errorDuration,
      context = {}
    } = options;
    
    if (!window.toast || !window.toast.promise) {
      try {
        const result = await promise;
        window.dispatchCardAction(action, 'success', successMessage, { context, position, duration: successDuration });
        return result;
      } catch (error) {
        window.dispatchCardAction(action, 'error', errorMessage || error.message, { context, position, duration: errorDuration });
        throw error;
      }
    }
    
    return window.toast.promise(loadingMessage, promise, {
      successMessage,
      errorMessage,
      position,
      successDuration,
      errorDuration,
      context
    });
  };

  console.log('Toast notification system initialized with enhanced configuration');
  return true;
}

/**
 * Add custom CSS to document for toast styling if not already present
 */
export function injectToastStyles() {
  if (!document.getElementById('toast-system-styles')) {
    const styleTag = document.createElement('style');
    styleTag.id = 'toast-system-styles';
    styleTag.textContent = `
      .toast-container {
        position: fixed;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-width: 360px;
        width: 100%;
        padding: 16px;
        box-sizing: border-box;
      }
      
      .toast-top-right {
        top: 0;
        right: 0;
      }
      
      .toast-top-left {
        top: 0;
        left: 0;
      }
      
      .toast-bottom-right {
        bottom: 0;
        right: 0;
      }
      
      .toast-bottom-left {
        bottom: 0;
        left: 0;
      }
      
      .toast-top-center {
        top: 0;
        left: 50%;
        transform: translateX(-50%);
      }
      
      .toast-bottom-center {
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
      }
      
      .toast {
        position: relative;
        display: flex;
        align-items: center;
        padding: 12px 16px;
        border-radius: 4px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        background-color: #fff;
        cursor: pointer;
        min-height: 48px;
        overflow: hidden;
        animation: toast-in 0.3s ease forwards;
      }
      
      @keyframes toast-in {
        from {
          transform: translateY(20px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      .toast-success {
        border-left: 4px solid #52c41a;
      }
      
      .toast-error {
        border-left: 4px solid #f5222d;
      }
      
      .toast-info {
        border-left: 4px solid #1890ff;
      }
      
      .toast-warning {
        border-left: 4px solid #faad14;
      }
      
      .toast-loading {
        border-left: 4px solid #722ed1;
      }
      
      .toast-icon {
        margin-right: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .toast-content {
        flex: 1;
        margin-right: 16px;
      }
      
      .toast-action {
        margin-top: 8px;
      }
      
      .toast-close {
        background: transparent;
        border: none;
        color: #999;
        cursor: pointer;
        font-size: 18px;
        line-height: 1;
        padding: 0;
        margin: 0;
      }
      
      .toast-close:hover {
        color: #666;
      }
      
      .toast-progress-container {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 4px;
        background-color: rgba(0, 0, 0, 0.05);
      }
      
      .toast-progress-bar {
        height: 100%;
        background-color: rgba(0, 0, 0, 0.1);
        transition: width 0.1s linear;
      }
      
      .toast-success .toast-progress-bar {
        background-color: rgba(82, 196, 26, 0.6);
      }
      
      .toast-error .toast-progress-bar {
        background-color: rgba(245, 34, 45, 0.6);
      }
      
      .toast-info .toast-progress-bar {
        background-color: rgba(24, 144, 255, 0.6);
      }
      
      .toast-warning .toast-progress-bar {
        background-color: rgba(250, 173, 20, 0.6);
      }
    `;
    document.head.appendChild(styleTag);
    return true;
  }
  return false;
}

// Named export object
const toastNotification = {
  initToastSystem,
  setupToastListeners,
  TOAST_TYPES,
  TOAST_POSITIONS,
  injectToastStyles
};

export default toastNotification;